import { useEffect, useRef, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { HorizontalSpacer, VerticalSpacer } from "../atoms/Spacers";
import { Col, Row } from "react-grid-system";
import Breadcrumb from "../molecules/Breadcrumb";
import BackButton from "../atoms/buttons/BackButton";
import { theme } from "../../theme";
import Title from "../atoms/typography/Title";
import Card from "../atoms/Card";
import BodySmall from "../atoms/typography/BodySmall";
import Small from "../atoms/typography/Small";
import { useClient } from "../../graphql/hooks/useClient";
import SmallLoadingSpinner from "../atoms/SmallLoadingSpinner";
import Body from "../atoms/typography/Body";
import Alert from "../molecules/Alert";
import styled from "styled-components";
import Subheading from "../atoms/typography/Subheading";
import RadioButton from "../atoms/inputs/RadioButton";
import TextField from "../atoms/inputs/TextField";
import PdfTemplate, { type CustomHighlight } from "../organisms/PdfTemplate";
import EditTemplateFields from "../organisms/template/EditTemplateFields";
import { useInvoiceReaderTemplate } from "../../graphql/hooks/invoiceInterpretationTemplates/useInvoiceReaderTemplate";
import type {
  InvoiceReaderEInvoiceTestedType,
  InvoiceReaderTemplateState,
  InvoiceReaderMaskingDataModel,
  StampInformation,
} from "../../models/ClientInvoiceReaderTemplateModel";
import OutlinedButton from "../atoms/buttons/OutlinedButton";
import ToggleButton from "../atoms/buttons/ToggleButton";
import { GridIcon } from "../atoms/icons/GridIcon";
import { ListIconView } from "../atoms/icons/ListIconView";
import { useSaveManager } from "../../hooks/useSaveManager";
import type { TemplateTabHandle, EditableMask } from "../../models/Templates";
import type { TemplateSettingsInput } from "../../models/InvoiceInterpretationInputs";
import { useUpdateOverview } from "../../graphql/hooks/invoiceInterpretationTemplates/useUpdateOverview";
import { usePreviewMaskAndStamp } from "../../graphql/hooks/invoiceInterpretationTemplates/usePreviewMaskAndStamp";
import {
  ChevronSmallRightIcon,
  ChevronSmallLeftIcon,
} from "../atoms/icons/ChevronIcons";
import {
  toTemplateMaskInput,
  toTemplateStampInput,
  findPageForPosition,
  findAllPagesForField,
} from "../../utils/templateHelpers";
import SidePanel from "../molecules/SidePanel";

const OutlinedDiv = styled.div`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey05};
  padding: 16px;
`;

const OutlinedCol = styled.div`
  border-radius: 4px;
  padding: 8px 0px;
`;

const FlexedDivCentered = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
`;
const FlexedDiv = styled.div`
  display: flex;
  gap: 32px;
`;
const NavigateButton = styled.button<{ disabled: boolean }>`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  justify-content: center;
  display: flex;
  align-items: center;
`;

const tabs = [
  { id: "templateValidation", tabLabelKey: "templates.validationFields" },
  { id: "mandatoryInvoiceFields", tabLabelKey: "templates.mandatoryFields" },
  { id: "optionalFields", tabLabelKey: "templates.optionalFields" },
  { id: "rowInterpretation", tabLabelKey: "templates.rowInterpretation" },
  { id: "stamp", tabLabelKey: "templates.stamp" },
  { id: "mask", tabLabelKey: "templates.mask" },
  { id: "settings", tabLabelKey: "clientCard.settings" },
] as const;

export type TabId = (typeof tabs)[number]["id"];

const EditTemplatePage: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id, templateId } = useParams();
  const [searchParams] = useSearchParams();
  const from = location.state?.from || undefined;
  const [displayHighlights, setDisplayHighlights] = useState<boolean>(true);
  const [isGridView, setIsGridView] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [numPages, setNumPages] = useState<number>(1);
  const saveManager = useSaveManager<TabId>();

  const currentTab = searchParams.get("tab") as TabId | null;

  const { client, error, loading } = useClient(id ?? "");

  const [highlightedFields, setHighlightedFields] = useState<CustomHighlight[]>(
    [],
  );

  const tabRefs = useRef<Record<TabId, TemplateTabHandle | null>>(
    Object.fromEntries(tabs.map((tab) => [tab.id, null])) as Record<
      TabId,
      TemplateTabHandle | null
    >,
  );

  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [pdfContainerHeight, setPdfContainerHeight] = useState(0);

  useEffect(() => {
    const el = pdfContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setPdfContainerHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const [
    generalTemplateSettingsHasChanges,
    setGeneralTemplateSettingsHasChanges,
  ] = useState(false);
  const [tabsHaveChanges, setTabsHaveChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Callback for tabs to notify when their dirty state changes
  const onTabDirtyChange = () => {
    setTabsHaveChanges(saveManager.hasUnsavedChanges());
  };

  const {
    template,
    error: templateError,
    loading: templateLoading,
    refetch,
  } = useInvoiceReaderTemplate(id ?? "", templateId ?? "");

  const refetchWrapper = async (): Promise<void> => {
    await refetch();
    // Wait for React to process the refetch and run useEffects that set dirty to false
    setTimeout(() => {
      onTabDirtyChange();
    }, 150);
  };

  const createEditableMask = (
    mask: InvoiceReaderMaskingDataModel,
  ): EditableMask => ({
    uiId: crypto.randomUUID(),
    maskingType: mask.maskingType,
    label: mask.label,
    value: mask.value,
  });

  const templateSettings: TemplateSettingsInput | undefined = template
    ? {
        withinLineMultiplier:
          template.extraction?.docstrumSettings?.withinLineMultiplier,
        withinLineLowerBound:
          template.extraction?.docstrumSettings?.withinLineLowerBound,
        withinLineUpperBound:
          template.extraction?.docstrumSettings?.withinLineUpperBound,
        dateFormat: template.extraction?.extractionSettings?.dateFormat,
      }
    : undefined;

  const [maskingDataState, setMaskingDataState] = useState<EditableMask[]>([]);
  const [activeMaskId, setActiveMaskId] = useState<string | null>(null);

  const [stampInformationState, setStampInformationState] =
    useState<StampInformation>();

  const [templateStatus, setTemplatestatus] =
    useState<InvoiceReaderTemplateState>(template?.state ?? "UNKNOWN");
  const [eInvoiceTestedStatus, setEInvoiceTestedStatus] =
    useState<InvoiceReaderEInvoiceTestedType>(
      template?.eInvoiceTested ?? "NOT_TESTED",
    );
  const [templateName, setTemplateName] = useState(template?.name ?? "");
  const [templateNotes, setTemplateNotes] = useState(template?.notes ?? "");

  const initializedTemplateId = useRef<string | null>(null);

  useEffect(() => {
    if (!template) return;

    if (initializedTemplateId.current === template.id) return;

    initializedTemplateId.current = template.id;

    setTemplatestatus(template.state);
    setEInvoiceTestedStatus(template.eInvoiceTested);
    setTemplateName(template.name ?? "");
    setTemplateNotes(template.notes ?? "");

    setMaskingDataState(
      template.extraction?.maskingData?.map(createEditableMask) ?? [],
    );

    const stamp = template.extraction?.stampInformation;
    setStampInformationState(
      stamp && !stamp.position
        ? {
            ...stamp,
            position: {
              bottomLeftX: 0,
              bottomLeftY: 0,
              topRightX: 360,
              topRightY: 60,
            },
          }
        : stamp,
    );
  }, [template]);

  const onToggleDisplayHighlights = () => {
    setDisplayHighlights(!displayHighlights);
  };

  const onSelectTemplateStatus = (
    templatestatus: InvoiceReaderTemplateState,
  ) => {
    setTemplatestatus(templatestatus);
    setGeneralTemplateSettingsHasChanges(true);
  };

  const onChangeEInvoiceTestedStatus = (
    eInvoiceTestedStatus: InvoiceReaderEInvoiceTestedType,
  ) => {
    setEInvoiceTestedStatus(eInvoiceTestedStatus);
    setGeneralTemplateSettingsHasChanges(true);
  };

  const {
    updateOverview,
    loading: overviewLoading,
    error: overviewError,
  } = useUpdateOverview({
    onCompleted: () => {
      setGeneralTemplateSettingsHasChanges(false);
      refetchWrapper();
    },
    onError: (err) => {
      console.error("Error saving overview:", err);
    },
  });

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState<string>("");
  const [previewPage, setPreviewPage] = useState(1);
  const [previewNumPages, setPreviewNumPages] = useState(1);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const { previewMaskAndStamp, loading: previewLoading } =
    usePreviewMaskAndStamp({
      onCompleted: (result) => {
        const file = result?.invoiceInterpretation?.previewMaskAndStamp;
        if (file?.content) {
          setPreviewError(null);
          setPreviewContent(file.content);
          setPreviewPage(1);
          setIsPreviewOpen(true);
        }
      },
      onError: (err) => {
        setPreviewError(err?.message ?? t("errors.somethingWentWrong"));
      },
    });

  const onPreview = async () => {
    if (!template || !id) return;
    await previewMaskAndStamp({
      templateId: template.id,
      clientIdentifier: id,
      maskInput: maskingDataState
        .map(toTemplateMaskInput)
        .filter((m) => m !== null),
      stampInput: toTemplateStampInput(stampInformationState),
    });
  };

  const canPreview =
    !previewLoading &&
    !!template &&
    (maskingDataState.length > 0 || !!stampInformationState?.position) &&
    !!template.extraction?.verifierPositions?.verifierLabel &&
    !!template.extraction?.verifierPositions?.extractionVerifier &&
    !!template.extraction?.fieldExtractionData.some(
      (f) => f.fieldType === "INVOICE_NUMBER" && f.value,
    );

  const onSaveOverview = async () => {
    if (!template || !id) return;

    await updateOverview({
      templateId: template.id,
      clientIdentifier: id,
      input: {
        state: templateStatus,
        eInvoiceTested: eInvoiceTestedStatus,
        name: templateName,
        notes: templateNotes,
      },
    });
  };

  return (
    <div>
      <SidePanel
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title={t("common.preview")}
        width="fit-content"
        headerActions={
          previewNumPages > 1 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <NavigateButton
                disabled={previewPage <= 1}
                onClick={() => setPreviewPage(previewPage - 1)}
              >
                <ChevronSmallLeftIcon color={theme.colors.textSecondary} />
              </NavigateButton>
              <BodySmall color={theme.colors.textSecondary}>
                {previewPage} / {previewNumPages}
              </BodySmall>
              <NavigateButton
                disabled={previewPage >= previewNumPages}
                onClick={() => setPreviewPage(previewPage + 1)}
              >
                <ChevronSmallRightIcon color={theme.colors.textSecondary} />
              </NavigateButton>
            </div>
          ) : undefined
        }
      >
        <Small color={theme.colors.textSecondary}>
          {t("templates.previewMaskInfo")}
        </Small>
        <HorizontalSpacer spacing={1} />
        <PdfTemplate
          fileBase64={previewContent}
          possibleFields={[]}
          displayHighlights={false}
          currentPage={previewPage}
          onNumPagesChange={setPreviewNumPages}
        />
      </SidePanel>
      <Row nogutter justify="between">
        <div>
          {from && (
            <Breadcrumb
              from={from}
              currentTitle={t("templates.editTemplate")}
            />
          )}
          <HorizontalSpacer spacing={1} />
          <Row align="center" nogutter>
            <BackButton
              onClick={() => navigate(-1)}
              color={theme.colors.primaryBg}
            />
            <VerticalSpacer spacing={1.5} />
            <Title textalign="left" color={theme.colors.primary}>
              {t("templates.editTemplate")}
            </Title>
          </Row>
        </div>
        <div>
          {(error ||
            templateError ||
            previewError ||
            saveError ||
            overviewError) && (
            <Alert
              type="error"
              message={
                error?.toString() ??
                templateError?.toString() ??
                previewError ??
                saveError ??
                overviewError?.message ??
                undefined
              }
              title={t("errors.somethingWentWrong")}
            />
          )}
        </div>
      </Row>

      <HorizontalSpacer spacing={2} />
      <Card>
        {templateLoading && <SmallLoadingSpinner />}
        <Row gutterWidth={16}>
          <Col lg={isGridView ? 5 : 12} xxl={isGridView ? 6 : 12}>
            <Row
              nogutter
              justify="between"
              align="center"
              style={{ rowGap: 8 }}
            >
              <div>
                <BodySmall color={theme.colors.textSecondary}>
                  {t("templates.templateType")} {template?.templateType}{" "}
                  {template?.templateLanguage}
                </BodySmall>
                {loading && <SmallLoadingSpinner />}
                <Body>{client?.name}</Body>
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                <ToggleButton
                  iconOn={<GridIcon color="white" />}
                  iconOff={<GridIcon color={theme.colors.primary} />}
                  isOn={isGridView}
                  onToggle={() => setIsGridView(!isGridView)}
                  size="medium"
                />
                <ToggleButton
                  iconOn={<ListIconView color="white" />}
                  iconOff={<ListIconView color={theme.colors.primary} />}
                  isOn={!isGridView}
                  onToggle={() => setIsGridView(!isGridView)}
                  size="medium"
                />
                {(template?.templateType === "DEBIT" ||
                  template?.templateType === "DEBIT_MASK_AND_STAMP_ONLY") && (
                  <OutlinedButton
                    title={t("common.preview")}
                    color={theme.colors.primary}
                    medium
                    disabled={!canPreview}
                    onClick={onPreview}
                  />
                )}
                <OutlinedButton
                  title={
                    overviewLoading ? t("random.saving") : t("common.save")
                  }
                  color={theme.colors.primary}
                  medium
                  disabled={
                    (!tabsHaveChanges && !generalTemplateSettingsHasChanges) ||
                    overviewLoading
                  }
                  onClick={async () => {
                    setSaveError(null);
                    try {
                      if (generalTemplateSettingsHasChanges) {
                        await onSaveOverview();
                      }
                      await saveManager.saveAll();
                      await refetch();
                      onTabDirtyChange();
                    } catch (err: any) {
                      setSaveError(
                        err?.message || t("errors.somethingWentWrong"),
                      );
                    }
                  }}
                />
              </div>
            </Row>

            <HorizontalSpacer spacing={2} />

            <OutlinedDiv>
              <Row
                nogutter
                justify="between"
                align="center"
                style={{ height: 24 }}
              >
                <Subheading color={theme.colors.primary}>
                  {t("templates.templateInformation")}
                </Subheading>
              </Row>
              <HorizontalSpacer spacing={1} />
              <div>
                <OutlinedCol>
                  <BodySmall>{t("templates.templateStatus")}</BodySmall>
                  <HorizontalSpacer spacing={0.5} />
                  <FlexedDivCentered>
                    <RadioButton
                      title={t("common.active")}
                      checked={templateStatus === "ACTIVE"}
                      onChange={() => onSelectTemplateStatus("ACTIVE")}
                    />
                    <RadioButton
                      title={t("random.incomplete")}
                      checked={templateStatus === "INCOMPLETE"}
                      onChange={() => onSelectTemplateStatus("INCOMPLETE")}
                    />
                    <RadioButton
                      title={t("templates.disabled")}
                      checked={templateStatus === "DISABLED"}
                      onChange={() => onSelectTemplateStatus("DISABLED")}
                    />
                  </FlexedDivCentered>
                </OutlinedCol>
                <OutlinedCol>
                  <BodySmall>{t("templates.testedForEInvoice")}</BodySmall>
                  <HorizontalSpacer spacing={0.5} />
                  <FlexedDivCentered>
                    <RadioButton
                      title={t("templates.working")}
                      checked={eInvoiceTestedStatus === "WORKING"}
                      onChange={() => onChangeEInvoiceTestedStatus("WORKING")}
                    />
                    <RadioButton
                      title={t("templates.notWorking")}
                      checked={eInvoiceTestedStatus === "NOT_WORKING"}
                      onChange={() =>
                        onChangeEInvoiceTestedStatus("NOT_WORKING")
                      }
                    />
                    <RadioButton
                      title={t("templates.notTested")}
                      checked={eInvoiceTestedStatus === "NOT_TESTED"}
                      onChange={() =>
                        onChangeEInvoiceTestedStatus("NOT_TESTED")
                      }
                    />
                  </FlexedDivCentered>
                </OutlinedCol>
              </div>

              <HorizontalSpacer spacing={1} />

              <FlexedDiv style={{ width: "99%" }}>
                <TextField
                  label={t("documentHandler.templateName")}
                  value={templateName}
                  onChange={(value) => {
                    setTemplateName(value);
                    setGeneralTemplateSettingsHasChanges(true);
                  }}
                  fullWidth
                />
                <TextField
                  label={t("common.comment")}
                  value={templateNotes}
                  onChange={(value) => {
                    setTemplateNotes(value);
                    setGeneralTemplateSettingsHasChanges(true);
                  }}
                  fullWidth
                />
              </FlexedDiv>
            </OutlinedDiv>

            <HorizontalSpacer spacing={2} />
            <OutlinedDiv
              style={
                isGridView && pdfContainerHeight > 0
                  ? { minHeight: pdfContainerHeight }
                  : {}
              }
            >
              {templateLoading ? (
                <SmallLoadingSpinner />
              ) : template ? (
                <EditTemplateFields
                  possibleFields={
                    template?.extraction?.possibleFieldValues ?? []
                  }
                  verifierPositions={template?.extraction?.verifierPositions}
                  highlightedFields={highlightedFields}
                  setHighlightedFields={setHighlightedFields}
                  onToggleDisplayHighlights={onToggleDisplayHighlights}
                  fields={template?.extraction?.fieldExtractionData ?? []}
                  rowExtractionData={
                    template?.extraction?.rowExtractionData ?? []
                  }
                  stampInformation={stampInformationState}
                  setStampInformation={setStampInformationState}
                  maskingData={maskingDataState}
                  templateSettings={templateSettings}
                  templateType={template.templateType}
                  tabRefs={tabRefs}
                  refetchTemplate={refetchWrapper}
                  saveManager={saveManager}
                  onUpdateMaskingDataState={(masks) =>
                    setMaskingDataState(masks)
                  }
                  activeMaskId={activeMaskId}
                  onUpdateActiveMaskId={(id) => setActiveMaskId(id)}
                  onTabDirtyChange={onTabDirtyChange}
                  onNavigateToPage={setCurrentPage}
                  totalPages={numPages}
                  onSaveError={setSaveError}
                  ignoredHeaders={template?.extraction?.ignoredHeaders}
                />
              ) : null}
            </OutlinedDiv>
          </Col>

          <Col lg={isGridView ? 7 : 12} xxl={isGridView ? 6 : 12}>
            {!isGridView && <HorizontalSpacer spacing={2} />}
            <OutlinedDiv
              ref={pdfContainerRef}
              style={isGridView ? { position: "sticky", top: 16 } : {}}
            >
              <Row nogutter justify="between" align="center">
                <Subheading color={theme.colors.primary}>
                  {t("invoices.invoice")}
                </Subheading>
                {numPages > 1 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <NavigateButton
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <ChevronSmallLeftIcon
                        color={theme.colors.textSecondary}
                      />
                    </NavigateButton>
                    <BodySmall color={theme.colors.textSecondary}>
                      {currentPage} / {numPages}
                    </BodySmall>
                    <NavigateButton
                      disabled={currentPage >= numPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      <ChevronSmallRightIcon
                        color={theme.colors.textSecondary}
                      />
                    </NavigateButton>
                  </div>
                )}
              </Row>
              <HorizontalSpacer spacing={1} />

              <PdfTemplate
                fileBase64={template?.file?.content ?? ""}
                possibleFields={template?.extraction?.possibleFieldValues ?? []}
                customHighlights={highlightedFields}
                displayHighlights={displayHighlights}
                currentPage={currentPage}
                onNumPagesChange={setNumPages}
                stampsEditable={currentTab === "stamp"}
                maskOverlays={maskingDataState
                  .map((mask, index) => {
                    const labelLabel = `mask-${index}-label`;
                    const valueLabel = `mask-${index}-value`;
                    const isToggled = highlightedFields.some(
                      (h) => h.label === labelLabel || h.label === valueLabel,
                    );

                    return {
                      mask,
                      index,
                      isToggled,
                    };
                  })
                  .filter(
                    (item) =>
                      item.mask.maskingType !== "REMOVE_LABEL_AND_VALUE" &&
                      item.mask.value?.position != null,
                  )
                  .map((item) => {
                    const possibleFieldValues =
                      template?.extraction?.possibleFieldValues ?? [];
                    const labelPages =
                      item.mask.maskingType ===
                        "REMOVE_ABSOLUTE_POSITION_ONLY_WHERE_LABEL_EXISTS" &&
                      item.mask.label?.position
                        ? findAllPagesForField(
                            item.mask.label.value,
                            item.mask.label.position,
                            possibleFieldValues,
                          )
                        : [];

                    return {
                      id: item.mask.uiId,
                      position: item.mask.value!.position,
                      ...(labelPages.length > 0
                        ? { showOnPages: labelPages }
                        : item.mask.maskingType ===
                              "REMOVE_ABSOLUTE_POSITION_ONLY_WHERE_LABEL_EXISTS" &&
                            item.mask.label?.position
                          ? {
                              pageNumber: findPageForPosition(
                                item.mask.label.position,
                                possibleFieldValues,
                              ),
                            }
                          : {}),
                      isActive:
                        currentTab === "mask" &&
                        item.mask.uiId === activeMaskId,
                      isToggled: item.isToggled,
                      showOnAllPages:
                        item.mask.maskingType ===
                        "REMOVE_ABSOLUTE_POSITION_ON_EVERY_PAGE",
                      onChange:
                        currentTab === "mask"
                          ? (newPos) => {
                              if (item.mask.uiId !== activeMaskId) return;

                              setMaskingDataState((prev) => {
                                const maskTab = tabRefs.current["mask"];
                                maskTab?.markDirty?.();
                                return prev.map((m) =>
                                  m.uiId === item.mask.uiId
                                    ? {
                                        ...m,
                                        value: {
                                          ...m.value!,
                                          position: newPos,
                                        },
                                      }
                                    : m,
                                );
                              });
                            }
                          : () => {}, // No-op when not on mask tab
                      onSelect:
                        currentTab === "mask"
                          ? () => setActiveMaskId(item.mask.uiId)
                          : () => {}, // No-op when not on mask tab
                    };
                  })}
                activeMaskId={currentTab === "mask" ? activeMaskId : null}
                onActiveMaskChange={
                  currentTab === "mask" ? setActiveMaskId : () => {}
                }
                onHighlightResized={
                  currentTab &&
                  (
                    [
                      "templateValidation",
                      "mandatoryInvoiceFields",
                      "optionalFields",
                      "rowInterpretation",
                    ] as const
                  ).includes(
                    currentTab as
                      | "templateValidation"
                      | "mandatoryInvoiceFields"
                      | "optionalFields"
                      | "rowInterpretation",
                  )
                    ? (label, newPosition, pageNumber) => {
                        tabRefs.current[
                          currentTab as TabId
                        ]?.handleHighlightResized?.(
                          label,
                          newPosition,
                          pageNumber,
                        );
                      }
                    : undefined
                }
                onFieldClick={(field) => {
                  if (!currentTab) return;
                  tabRefs.current[currentTab]?.handleFieldClick?.(field);
                }}
                stampInformation={
                  template?.templateType !== "CREDIT"
                    ? stampInformationState
                    : undefined
                }
                onAlternativeStampChanged={
                  currentTab === "stamp"
                    ? (index, newPos) => {
                        setStampInformationState((prev) => {
                          if (!prev) return prev;
                          const newAlts = [...prev.alternativeStampPositions];
                          newAlts[index] = newPos;
                          return {
                            ...prev,
                            alternativeStampPositions: newAlts,
                          };
                        });
                        const maskTab = tabRefs.current["stamp"];
                        maskTab?.markDirty?.();
                      }
                    : () => {}
                }
                onStampPositionChanged={
                  currentTab === "stamp"
                    ? (newPos) => {
                        setStampInformationState((prev) => {
                          if (!prev) return prev;
                          return { ...prev, position: newPos };
                        });
                        const maskTab = tabRefs.current["stamp"];
                        maskTab?.markDirty?.();
                      }
                    : () => {}
                }
              />
            </OutlinedDiv>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default EditTemplatePage;
